import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, CreditCard, Calendar, RefreshCw, ArrowLeft } from "lucide-react";
import {
  getMyRentalStatus,
  syncMySubscription,
  cancelMySubscription,
  resumeMySubscription,
} from "@/lib/subscription.functions";

const STRIPE_PAYMENT_URL = "https://buy.stripe.com/14A4gy12K2WM0ZS8uD6c00q";

export const Route = createFileRoute("/_authenticated/najemnina")({
  head: () => ({
    meta: [
      { title: "Najemnina trgovine — kupish.si" },
      { name: "description", content: "Pregled statusa letne najemnine vaše spletne trgovine na kupish.si, datum naslednjega plačila in upravljanje naročnine." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: RentalPage,
});

function fmtDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("sl-SI", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function statusLabel(status: string, canceled: boolean) {
  if (canceled) return "Prekinitev ob koncu obdobja";
  switch (status) {
    case "active": return "Aktivna";
    case "trialing": return "Poskusno obdobje";
    case "past_due": return "Zapadlo plačilo";
    case "unpaid": return "Neplačano";
    case "paused": return "Začasno ustavljena";
    case "incomplete": return "Nedokončana";
    case "canceled": return "Preklicana";
    default: return status;
  }
}

function RentalPage() {
  const qc = useQueryClient();
  const load = useServerFn(getMyRentalStatus);
  const syncFn = useServerFn(syncMySubscription);
  const cancelFn = useServerFn(cancelMySubscription);
  const resumeFn = useServerFn(resumeMySubscription);

  const { data, isLoading, error } = useQuery({
    queryKey: ["my-rental-status"],
    queryFn: () => load(),
    refetchOnWindowFocus: true,
  });

  const syncMut = useMutation({
    mutationFn: () => syncFn(),
    onSuccess: (res) => {
      if (res.ok && res.synced > 0) {
        toast.success(res.activated ? "Trgovina je aktivirana." : "Status posodobljen.");
      } else if (res.ok) {
        toast.info("Status je že aktualen.");
      }
      qc.invalidateQueries({ queryKey: ["my-rental-status"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelFn(),
    onSuccess: async () => {
      toast.success("Naročnina bo prekinjena ob koncu obračunskega obdobja.");
      await syncFn().catch(() => {});
      qc.invalidateQueries({ queryKey: ["my-rental-status"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resumeMut = useMutation({
    mutationFn: () => resumeFn(),
    onSuccess: async () => {
      toast.success("Naročnina obnovljena.");
      await syncFn().catch(() => {});
      qc.invalidateQueries({ queryKey: ["my-rental-status"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="mx-auto max-w-3xl px-6 py-24 text-muted-foreground">Nalagam…</div>;
  }
  if (error) {
    return <div className="mx-auto max-w-3xl px-6 py-24 text-destructive">Napaka: {(error as Error).message}</div>;
  }
  if (!data) return null;

  const sub = data.subscription;
  const canceled = Boolean(sub?.cancel_at_period_end);
  const active = sub?.status === "active" || sub?.status === "trialing";
  const email = data.email;
  const payHref = email ? `${STRIPE_PAYMENT_URL}?prefilled_email=${encodeURIComponent(email)}` : STRIPE_PAYMENT_URL;

  return (
    <article className="mx-auto max-w-3xl px-6 py-12 md:py-16">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Nazaj na Moj račun
      </Link>

      <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Za prodajalce</div>
          <h1 className="font-serif text-4xl mt-2">
            Najemnina <span className=" text-accent">trgovine</span>
          </h1>
        </div>
        <button
          type="button"
          onClick={() => syncMut.mutate()}
          disabled={syncMut.isPending}
          className="inline-flex items-center gap-2 border border-border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${syncMut.isPending ? "animate-spin" : ""}`} />
          {syncMut.isPending ? "Preverjam…" : "Preveri status pri Stripe"}
        </button>
      </div>

      {/* Status banner */}
      <div
        className={`mt-8 border p-6 flex items-start gap-4 ${
          active && !canceled
            ? "border-accent/40 bg-accent/5"
            : canceled
              ? "border-amber-500/40 bg-amber-500/5"
              : "border-destructive/40 bg-destructive/5"
        }`}
      >
        {active && !canceled ? (
          <CheckCircle2 className="h-6 w-6 text-accent shrink-0 mt-0.5" />
        ) : (
          <AlertCircle className={`h-6 w-6 shrink-0 mt-0.5 ${canceled ? "text-amber-600" : "text-destructive"}`} />
        )}
        <div className="flex-1">
          <div className="text-sm text-muted-foreground">Trenutni status</div>
          <div className="font-serif text-2xl mt-1">
            {sub ? statusLabel(sub.status, canceled) : "Brez aktivne naročnine"}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {active && !canceled && "Vaša trgovina je aktivna. Lahko urejate izdelke in prejemate naročila."}
            {active && canceled && sub?.current_period_end &&
              `Trgovina ostane aktivna do ${fmtDate(sub.current_period_end)}. Nato dostop do urejanja preneha.`}
            {!active && sub &&
              "Trgovina je trenutno neaktivna. Za nadaljevanje prodaje obnovite naročnino."}
            {!sub &&
              "Za aktivacijo trgovine je potrebna letna najemnina."}
          </p>
        </div>
      </div>

      {/* Details grid */}
      {sub && (
        <div className="mt-6 border border-border">
          <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border">
            <Detail
              icon={<CreditCard className="h-4 w-4" />}
              label="Najem"
              value={sub.product_name ?? "Najem spletne trgovine"}
              hint={sub.amount != null ? `${sub.amount} ${sub.currency.toUpperCase()}${sub.interval ? " / " + intervalLabel(sub.interval) : ""}` : null}
            />
            <Detail
              icon={<Calendar className="h-4 w-4" />}
              label={canceled ? "Trgovina aktivna do" : "Naslednja obremenitev"}
              value={fmtDate(sub.current_period_end)}
              hint={sub.current_period_start ? `Obdobje od ${fmtDate(sub.current_period_start)}` : null}
            />
          </div>
          <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border border-t border-border">
            <Detail label="Začetek najema" value={fmtDate(sub.started_at)} />
            <Detail
              label="Preklic"
              value={sub.canceled_at ? fmtDate(sub.canceled_at) : canceled ? "V teku (ob koncu obdobja)" : "—"}
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-8 flex flex-wrap gap-3">
        {sub && canceled ? (
          <button
            onClick={() => resumeMut.mutate()}
            disabled={resumeMut.isPending}
            className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50"
          >
            {resumeMut.isPending ? "Obnavljam…" : "Obnovi naročnino"}
          </button>
        ) : sub && active ? (
          <button
            onClick={() => {
              if (confirm("Ste prepričani, da želite preklicati naročnino? Naslednje plačilo se ne bo izvedlo.")) {
                cancelMut.mutate();
              }
            }}
            disabled={cancelMut.isPending}
            className="border border-destructive text-destructive px-5 py-2.5 text-sm hover:bg-destructive/10 disabled:opacity-50"
          >
            {cancelMut.isPending ? "Preklicam…" : "Prekliči naročnino"}
          </button>
        ) : (
          <a
            href={payHref}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:opacity-90"
          >
            Aktiviraj naročnino →
          </a>
        )}
      </div>

      <p className="mt-8 text-xs text-muted-foreground">
        Status se samodejno posodobi ob prijavi in po spremembi naročnine. Če plačate ravnokar, kliknite <strong>Preveri status pri Stripe</strong> — trgovina se odklene takoj po potrditvi plačila.
      </p>
    </article>
  );
}

function Detail({ icon, label, value, hint }: { icon?: React.ReactNode; label: string; value: string; hint?: string | null }) {
  return (
    <div className="p-5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 font-medium">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function intervalLabel(interval: string) {
  switch (interval) {
    case "day": return "dan";
    case "week": return "teden";
    case "month": return "mesec";
    case "year": return "leto";
    default: return interval;
  }
}
